<template>
  <div :class="{ 'build': true, 'build--translation-mode': isTranslationMode, 'build--create-mode': !isUpdate }">
    <div class="build__form">
      <div
        class="checklist-editor-form"
        :class="{
          'checklist-editor-form--dragging': isDragging,
          'checklist-editor-form--ai-mode': isAiTranslationMode,
          'checklist-editor-form--manual-mode': isManualTranslationMode
        }"
      >
        <card class="px-5 mb-4 relative">
          <TranslationTooltip class="w-full" :active="isAiTranslationMode">
            <input
              :value="checklistTitle"
              :placeholder="titlePlaceholder"
              class="checklist-editor-form__title"
              :maxlength="titleLimit"
              :pattern="titlePattern"
              :readonly="isAiTranslationMode"
              @input="onTitleChange"
            >
            <p v-if="showWarningMessage" class="help is-warning">
              <font-awesome-icon v-once :icon="['fal', 'info-circle']" class="checklist-editor-form__title-warning-icon" />
              {{ $t('checklist.title-warning') }}
            </p>
          </TranslationTooltip>

          <TranslationTooltip class="checklist-editor-form__category-tooltip" :active="isTranslationMode" :is-ai="isAiTranslationMode">
            <div class="absolute checklist-editor-form__category">
              <Categories
                :checklist="checklist"
                :disabled="isAiTranslationMode"
                :unavailable="!hasFeature('analytics')"
                :categories="categories"
              />
            </div>
          </TranslationTooltip>

          <TranslationTooltip class="w-full" :active="isAiTranslationMode">
            <textarea
              :value="checklistDescription"
              :placeholder="descriptionPlaceholder"
              rows="1"
              class="w-full text-80 px-3 py-5"
              :readonly="isAiTranslationMode"
              @input="onDescriptionChange"
            ></textarea>
          </TranslationTooltip>
        </card>

        <Form :item="checklist.form" />

        <div class="mt-3 mb-6">
          <template v-if="showActionButtons">
            <TranslationTooltip :active="isTranslationMode" :is-ai="isAiTranslationMode">
              <b-button
                class="checklist-builder__bottom-action-buttons"
                icon-left="plus"
                type="is-text"
                @click="addQuestion"
              >
                {{ $t('template-builder.add-query-instruction') }}
              </b-button>
            </TranslationTooltip>

            <TranslationTooltip :active="isTranslationMode" :is-ai="isAiTranslationMode">
              <b-button
                class="checklist-builder__bottom-action-buttons"
                icon-left="plus"
                type="is-text"
                @click="addSection"
              >
                {{ $t('template-builder.add-section') }}
              </b-button>
            </TranslationTooltip>

            <TranslationTooltip :active="isTranslationMode" :is-ai="isAiTranslationMode">
              <b-button
                v-if="canHaveLogic"
                class="checklist-builder__bottom-action-buttons"
                icon-left="plus"
                type="is-text"
                @click="addLogic"
              >
                {{ $t('template-builder.add-logic') }}
              </b-button>
            </TranslationTooltip>
          </template>

          <TranslationTooltip :active="isTranslationMode" :is-ai="isAiTranslationMode">
            <b-button class="checklist-builder__bottom-action-buttons" icon-left="plus" type="is-text" @click="addPage">
              {{ $t('add page') }}
            </b-button>
          </TranslationTooltip>
        </div>

        <portal to="modals">
          <transition name="fade">
            <DeleteItemModal
              v-if="deleteModalOpen"
              @confirm="deleteItem"
              @close="closeDeleteModal"
            />
            <EditRoccSeverityModal
              v-if="editRoccModalOpen"
              @save="saveRoccSeverity"
              @close="closeRoccSeverity"
            />
            <ResponseSetEditorModal
              v-if="editResponseSetModalOpen"
              @close="closeResponseSetModal"
            />
            <ReorderPageModal
              v-if="isReorderPageModalOpen"
              :pages="pages"
              @confirm="reorderPageModalSaved"
              @close="reorderPageModalClosed"
            />

            <IssueCreatorModal
              v-if="isIssueItemModalOpen"
              :issue="issueBeingEdited"
              @save="createIssue"
              @close="issueItemModalClosed"
            />

            <InspectionActionCreatorModal
              v-if="isInspectionItemModalOpen"
              :inspection-action="inspectionBeingEdited"
              @save="createInspectionAction"
              @close="inspectionItemModalClosed"
            />
          </transition>
        </portal>
      </div>
    </div>

    <div class="build__preview">
      <div ref="previewContainer">
        <h3>{{ $t('Preview') }}</h3>
        <PhoneFrame @scale="phoneFrameScaled">
          <ConductInspectionPreview
            preview-mode
            :inspection="previewChecklist"
            :on-submit="() => {}"
            @cancel="() => {}"
          />
        </PhoneFrame>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import authMixin from '@/_legacy/mixins/authMixin';
  import Form from '@/_legacy/views/ChecklistBuilder/Build/Items/Form';
  import DeleteItemModal from '@/_legacy/views/ChecklistBuilder/Build/Modals/DeleteItem.vue';
  import EditRoccSeverityModal from '@/_legacy/views/ChecklistBuilder/Build/Modals/EditRoccSeverity.vue';
  import ResponseSetEditorModal from '@/_legacy/views/ChecklistBuilder/Build/Modals/EditResponseSet.vue';
  import ReorderPageModal from '@/_legacy/views/ChecklistBuilder/Build/Modals/ReorderPage.vue';
  import IssueItemModal from '@/_legacy/views/ChecklistBuilder/Build/Modals/IssueItemModal.vue';
  import PhoneFrame from '@/_legacy/components/PhoneFrame.vue';
  import ConductInspectionPreview from '@/_legacy/components/ConductInspection/ConductInspectionPreview.vue';
  import Categories from '@/_legacy/views/ChecklistBuilder/Settings/Categories.vue';
  import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
  import IconInfoCircle from '@/_legacy/components/Icons/IconInfoCircle';
  import AddPageIcon from '@/_legacy/components/Icons/AddPageIcon';
  import { Button as BButton } from '@/ui/components/Button';
  import { INSPECTION_ITEM_RESPONSE, INSPECTION_ITEM_TYPE } from '@/_legacy/constants';
  import { findItemParent, findQuestions } from '@/_legacy/store/checklist_builder';
  import { IssueCreatorModal } from '@/ui/components/checklist/IssueCreatorModal';
  import { InspectionActionCreatorModal } from '@/ui/components/checklist/InspectionActionCreatorModal';
  import {
    issueBeingEdited,
    setIssueBeingEditedFromLegacy
  } from '@/ui/pages/checklist/ChecklistBuilderIssueTemplateStore';
  import {
    checklistBeingEdited,
    setInspectionBeingEditedFromLegacy
  } from '@/ui/pages/checklist/ChecklistBuilderChecklistTemplateStore';
  import { stringLengthLimit } from '@/ui/helpers/patternHelper';
  import { useTranslationLanguagesStore } from '@/stores/checklist/translationLanguages';
  import TranslationTooltip from '@/_legacy/views/ChecklistBuilder/TranslationTooltip.vue';
  import { useBuilderMode } from '@/ui/concerns/checklist/useBuilderMode';
  import { useManualTranslationsStore } from '@/stores/checklist/manualTranslations';
  import { debounce } from 'lodash';

  const titleLimit = 255;

  export default {
    name: 'Build',
    components: {
      TranslationTooltip,
      AddPageIcon,
      Categories,
      DeleteItemModal,
      EditRoccSeverityModal,
      ResponseSetEditorModal,
      ReorderPageModal,
      IssueItemModal,
      Form,
      PhoneFrame,
      ConductInspectionPreview,
      IconInfoCircle,
      BButton,
      IssueCreatorModal,
      InspectionActionCreatorModal,
    },
    mixins: [authMixin],
    props: {
      resourceId: {
        type: String,
        default: '',
      },
      isCloning: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      lastRetrievedAt: {
        type: Number,
        default: null,
      },
    },
    setup() {
      const translationLanguagesStore = useTranslationLanguagesStore();
      const manualTranslationsStore = useManualTranslationsStore();
      const { isAiTranslationMode, isManualTranslationMode, isTranslationMode } = useBuilderMode();

      return {
        issueBeingEdited,
        inspectionBeingEdited: checklistBeingEdited,
        translationLanguagesStore,
        manualTranslationsStore,
        isAiTranslationMode,
        isManualTranslationMode,
        isTranslationMode,
      };
    },
    data() {
      return {
        previewChecklist: {},
        titleLimit,
        manualTitle: null,
        manualDescription: null,
      };
    },
    computed: {
      ...mapGetters({
        isReorderPageModalOpen: 'checklist_builder/isReorderPageModalOpen',
        isIssueItemModalOpen: 'checklist_builder/isIssueItemModalOpen',
        isInspectionItemModalOpen: 'checklist_builder/isInspectionItemModalOpen',
        isDragging: 'checklist_builder/isDragging',
        selectedIssueItem: 'checklist_builder/selectedIssueItem',
        selectedInspectionItem: 'checklist_builder/selectedInspectionItem',
        assignees: 'checklist_builder/users',
        entityItems: 'checklist_builder/entityItems',
        categories: 'checklist_builder/categories',
        checklist: 'checklist_builder/checklist',
        lastPage: 'checklist_builder/lastPage',
      }),
      showActionButtons() {
        return this.pages.length > 1;
      },
      pages: {
        get() {
          return this.$store.getters['checklist_builder/pages'];
        },
        set(payload) {
          this.$store.dispatch(
            'checklist_builder/pageReordered',
            payload,
          );
        },
      },
      dragOptions() {
        return {
          animation: 200,
          ghostClass: 'dragging-checklist-item-ghost',
          chosenClass: 'dragging-checklist-item-chosen',
          dragClass: 'dragging-checklist-item-drag',
          forceFallback: true,
        };
      },
      title() {
        if (this.isCloning) {
          return this.$t('Clone checklist');
        }
        if (this.isUpdate) {
          return this.$t('Edit checklist');
        }
        return this.$t('Create checklist');
      },
      isUpdate() {
        return this.resourceId && !this.isCloning;
      },
      deleteModalOpen() {
        return this.$store.getters['checklist_builder/delete_item_modal'].open;
      },
      editRoccModalOpen() {
        return this.$store.getters['checklist_builder/edit_rocc_modal'].open;
      },
      editResponseSetModalOpen() {
        return this.$store.getters['checklist_builder/edit_response_set_modal'].open;
      },
      canHaveLogic() {
        const lastQuestion = findQuestions([this.lastPage]).pop();
        if (!lastQuestion) {
          return false;
        }

        switch (lastQuestion.response_type) {
          case INSPECTION_ITEM_RESPONSE.LIST:
          case INSPECTION_ITEM_RESPONSE.CHECKBOX:
          case INSPECTION_ITEM_RESPONSE.NUMBER:
          case INSPECTION_ITEM_RESPONSE.ENTITY_ITEM:
          case INSPECTION_ITEM_RESPONSE.TEXT:
            return true;
          default:
            return false;
        }
      },
      showWarningMessage() {
        return this.checklist.checklist_title.length >= titleLimit;
      },
      titlePattern() {
        return stringLengthLimit(titleLimit);
      },
      checklistTitle() {
        return this.isTranslationMode ? this.checklist.checklist_title_translated : this.checklist.checklist_title;
      },
      checklistDescription() {
        return this.isTranslationMode
          ? this.checklist.checklist_description_translated
          : this.checklist.checklist_description;
      },
      titlePlaceholder() {
        if (this.isManualTranslationMode && this.checklist.checklist_title) {
          return this.checklist.checklist_title;
        }

        return this.$t('Enter list name');
      },
      descriptionPlaceholder() {
        if (this.isManualTranslationMode && this.checklist.checklist_description) {
          return this.checklist.checklist_description;
        }

        return this.$t('Enter description (optional)');
      },
    },
    watch: {
      isDragging(isDragging) {
        if (isDragging) {
          document.body.classList.add('checklist-editor-body--dragging');
        } else {
          document.body.classList.remove(
            'checklist-editor-body--dragging',
          );
        }
      },
      pages: {
        deep: true,
        handler(pages) {
          const page = pages.find(page => page.params.header);
          // page can be null or undefined
          if (page && page.items.length !== 2) {
            trackEvent(EventTypes.customizeHeader, EVENTS.HEADER_CUSTOMIZED);
          }
        },
      },
      checklist: {
        deep: true,
        immediate: true,
        handler(value) {
          this.previewChecklist = { ...value };
        },
      },
      selectedIssueItem(issue) {
        if (issue) {
          setIssueBeingEditedFromLegacy(issue);
        } else {
          issueBeingEdited.value = null;
        }
      },
      selectedInspectionItem(inspection) {
        if (inspection) {
          setInspectionBeingEditedFromLegacy(inspection);
        } else {
          checklistBeingEdited.value = null;
        }
      },
      manualTitle(newVal, oldVal) {
        this.debouncedSetManualTitle(newVal, oldVal);
      },
      manualDescription(newVal, oldVal) {
        this.debouncedSetManualDescription(newVal, oldVal);
      }
    },
    created() {
      if (this.isManualTranslationMode) {
        this.manualTitle = this.checklist.checklist_title_translated;
        this.manualDescription = this.checklist.checklist_description_translated;
        this.debouncedSetManualTitle = debounce(this.setManualTitle, 300);
        this.debouncedSetManualDescription = debounce(this.setManualDescription, 300);
      }
    },
    beforeDestroy() {
      this.debouncedSetManualTitle?.cancel();
      this.debouncedSetManualDescription?.cancel();
    },
    methods: {
      ...mapActions({
        reorderPageModalOpened: 'checklist_builder/reorderPageModalOpened',
        reorderPageModalSaved: 'checklist_builder/reorderPageModalSaved',
        reorderPageModalClosed: 'checklist_builder/reorderPageModalClosed',
        issueItemModalOpened: 'checklist_builder/issueItemModalOpened',
        issueItemModalSaved: 'checklist_builder/issueItemModalSaved',
        issueItemModalClosed: 'checklist_builder/issueItemModalClosed',
        inspectionItemModalOpened: 'checklist_builder/inspectionItemModalOpened',
        inspectionItemModalSaved: 'checklist_builder/inspectionItemModalSaved',
        inspectionItemModalClosed: 'checklist_builder/inspectionItemModalClosed',
      }),
      createIssue(issue) {
        const payload = {
          ...(this.selectedIssueItem?.node_id ? { node_id: this.selectedIssueItem.node_id } : {}),
          uuid: this.selectedIssueItem ? this.selectedIssueItem.uuid : null,
          title: issue.title.value,
          description: issue.description.value,
          // assignees later on is being encoded to
          // "assignees": { "users": [], "groups": null, "roles": null, "entityItems": null, "administrators": false }
          // in file `src/_legacy/store/checklist_builder/encoders.js`. At least it is expected to pass only
          // the users to the mutation in Vuex
          assignees: issue.assignees.value,
          associateAssignees: issue.associateAssignees.value,
          assignedGroups: issue.assignedGroups.value,
          priority: issue.priority.value,
          dueIn: issue.dueIn.value,
          showInspectorIssueIsRaised: issue.showIssueHasBeenCreated,
          entityItemSpecificAssignments: issue.entityItemSpecificAssignments,
          approvalsEnabled: issue.approvalsEnabled,
          approvalsEntitySpecificAssignments: issue.approvalsEntitySpecificAssignments,
          approvers: issue.approvers.value,
          settings: {
            assignees_enabled: issue.assignees.definedByInspector,
            assignees_required: issue.assignees.required,
            description_enabled: issue.description.definedByInspector,
            description_required: issue.description.required,
            due_after_enabled: issue.dueIn.definedByInspector,
            due_after_required: issue.dueIn.required,
            photos_enabled: issue.photos.definedByInspector,
            photos_required: issue.photos.required,
            priority_enabled: issue.priority.definedByInspector,
            priority_required: issue.priority.required,
            title_enabled: issue.title.definedByInspector,
            title_required: issue.title.required
          }
        };

        this.issueItemModalSaved(payload);
      },
      createInspectionAction(inspectionAction) {
        const payload = {
          uuid: this.selectedInspectionItem ? this.selectedInspectionItem.uuid : null,
          checklistId: inspectionAction.checklistId.value,
          title: inspectionAction.title.value,
          assignees: inspectionAction.assignees.value,
          assignedGroups: inspectionAction.assignedGroups.value,
          associateAssignees: inspectionAction.associateAssignees.value,
          dueIn: inspectionAction.dueIn.value,
        };
        this.inspectionItemModalSaved(payload);
      },
      addPage() {
        this.$store.dispatch('checklist_builder/addItem', {
          parent: this.checklist.form.uuid,
          item: {
            type: 'page',
            title: '',
            params: {
              collapsed: false,
            },
          },
        });
      },
      dragElementMoved(event) {
        return !event.relatedContext.element.params.header;
      },
      closeResponseSetModal() {
        this.$store.commit('checklist_builder/closeResponseSetEditorModal');
      },
      deleteItem() {
        this.$store.commit('checklist_builder/deleteItem');
      },
      saveRoccSeverity(data) {
        this.$store.commit('checklist_builder/saveRoccSeverity', data);
      },
      closeRoccSeverity() {
        this.$store.commit('checklist_builder/closeRoccModal');
      },
      closeDeleteModal() {
        this.$store.commit('checklist_builder/closeDeleteModal');
      },
      phoneFrameScaled({ height }) {
        const titleHeight = 30;
        this.$refs.previewContainer.style.height = `${height + titleHeight}px`;
      },
      addQuestion() {
        this.$store.dispatch('checklist_builder/addItem', {
          parent: this.lastPage.uuid,
          after: this.lastPage.items[this.lastPage.items.length - 1]?.uuid,
          prepend: null,
          item: {
            type: INSPECTION_ITEM_TYPE.QUESTION,
            title: '',
          },
        });
      },
      addSection() {
        this.$store.dispatch('checklist_builder/addItem', {
          parent: this.lastPage.uuid,
          after: this.lastPage.items[this.lastPage.items.length - 1]?.uuid,
          prepend: null,
          item: {
            type: INSPECTION_ITEM_TYPE.SECTION,
            title: '',
          },
        });
      },
      addLogic() {
        const questions = findQuestions([this.lastPage]);
        const lastQuestion = questions[questions.length - 1];
        const parent = findItemParent(this.lastPage, lastQuestion.uuid);

        this.$store.dispatch('checklist_builder/addItem', {
          parent: parent.uuid,
          after: lastQuestion.uuid,
          dependsOn: lastQuestion.uuid,
          item: {
            type: INSPECTION_ITEM_TYPE.CONDITIONAL,
          },
        });
      },
      onTitleChange(event) {
        if (this.isManualTranslationMode) {
          this.manualTitle = event.target.value;

          return;
        }

        this.checklist.checklist_title = event.target.value;
      },
      onDescriptionChange(event) {
        if (this.isManualTranslationMode) {
          this.manualDescription = event.target.value;

          return;
        }

        this.checklist.checklist_description = event.target.value;
      },
      setManualTitle(newVal, oldVal) {
        if (oldVal === null) {
          return;
        }

        this.manualTranslationsStore.setTitle(newVal, this.checklist.checklist_title_translated ?? '');
      },
      setManualDescription(newVal, oldVal) {
        if (oldVal === null) {
          return;
        }

        this.manualTranslationsStore.setDescription(newVal, this.checklist.checklist_description_translated ?? '');
      },
    },
  };
</script>
